// The haemodialysis session record (spec Q6): before, hourly during, after.
// Each section saves on its own. BP, pulse, temperature and weight from the
// before/after sections are also written to the IPD vitals through the nurse
// dashboard's own addVitals, so the doctor console and nurse dashboard see them
// — once per reading: a re-save that leaves the vitals unchanged copies nothing.

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";

import { fetchChart, saveChart } from "../api/daycareApi";
import { addVitals } from "../../nurse-dashboard/api/nurseApi";
import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdStatCard,
  IpdTextField,
} from "../../ipd/utils";
import { ACCESS_TYPES } from "../constants";

// [key, label, unit]
const PRE_NUMBERS = [
  ["weight_kg", "Weight", "kg"], ["dry_weight_kg", "Dry weight", "kg"], ["uf_goal_ml", "Fluid removal goal", "ml"],
  ["bp_systolic", "BP systolic", "mmHg"], ["bp_diastolic", "BP diastolic", "mmHg"], ["pulse", "Pulse", "/min"],
  ["temperature_f", "Temperature", "°F"], ["dialyser_reuse_no", "Dialyser reuse no.", ""], ["heparin_units", "Heparin", "U"],
];
const INTRA_NUMBERS = [
  ["bp_systolic", "Sys", "mmHg"], ["bp_diastolic", "Dia", "mmHg"], ["pulse", "Pulse", "/min"],
  ["blood_flow_ml_min", "Blood flow", "ml/min"], ["venous_pressure", "Venous P", "mmHg"], ["tmp", "TMP", "mmHg"],
  ["uf_rate_ml_h", "UF rate", "ml/h"], ["heparin_units", "Heparin", "U"],
];
const POST_NUMBERS = [
  ["weight_kg", "Weight", "kg"], ["uf_achieved_ml", "Fluid removed", "ml"], ["bp_systolic", "BP systolic", "mmHg"],
  ["bp_diastolic", "BP diastolic", "mmHg"], ["pulse", "Pulse", "/min"], ["temperature_f", "Temperature", "°F"],
];

const clean = (entry) => Object.fromEntries(Object.entries(entry).filter(([, v]) => v !== "" && v !== null));

function readingOf(section) {
  const s = section || {};
  const reading = {
    blood_pressure: s.bp_systolic && s.bp_diastolic ? `${s.bp_systolic}/${s.bp_diastolic}` : null,
    heart_rate: s.pulse || null,
    temperature: s.temperature_f || null,
    weight: s.weight_kg || null,
  };
  return Object.values(reading).some(Boolean) ? reading : null;
}

/** The IPD vitals row that mirrors a before/after section, or null if it has no vitals. */
export function vitalsFrom(section, admissionId, patientId) {
  const reading = readingOf(section);
  return reading ? { admission_id: admissionId, patient_id: patientId, ...reading } : null;
}

/** Identity of a section's vitals, so an unchanged re-save is not copied again. */
export function vitalsKey(section) {
  const reading = readingOf(section);
  return reading ? JSON.stringify(Object.values(reading).map(String)) : null;
}

const unitAdornment = (unit) => (unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined);

function NumberGrid({ fields, value, onChange, disabled }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 2 }}>
      {fields.map(([key, label, unit]) => (
        <IpdFormField key={key} label={label}>
          <IpdTextField fullWidth type="number" value={value[key] ?? ""} disabled={disabled}
            InputProps={unitAdornment(unit)} onChange={(e) => onChange({ ...value, [key]: e.target.value })} />
        </IpdFormField>
      ))}
    </Box>
  );
}

/** One part of the run: a titled panel whose save button sits in its header. */
function ChartPart({ title, subtitle, onSave, saving, readOnly, children }) {
  return (
    <Box sx={{ border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1, overflow: "hidden", mb: 2 }}>
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap",
        px: 2, py: 1.25, bgcolor: IPD_COLORS.panelHeaderBg, borderBottom: `1px solid ${IPD_COLORS.border}`,
      }}
      >
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: IPD_COLORS.textPrimary }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textSecondary }}>{subtitle}</Typography>}
        </Box>
        {!readOnly && (
          <IpdButton size="small" loading={saving} disabled={saving} onClick={onSave}>
            Save
          </IpdButton>
        )}
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

function Summary({ pre, post }) {
  if (!pre.weight_kg || !post.weight_kg) return null;
  const change = Number(post.weight_kg) - Number(pre.weight_kg);
  const items = [
    ["Weight change", `${change.toFixed(1)} kg`],
    pre.dry_weight_kg && ["Above dry weight", `${(Number(post.weight_kg) - Number(pre.dry_weight_kg)).toFixed(1)} kg`],
    post.uf_achieved_ml && ["Fluid removed", `${post.uf_achieved_ml} ml`],
    pre.uf_goal_ml && ["Removal goal", `${pre.uf_goal_ml} ml`],
  ].filter(Boolean);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 2, mb: 2 }}>
      {items.map(([label, value]) => <IpdStatCard key={label} label={label} value={value} />)}
    </Box>
  );
}

export default function DialysisChart({ session, readOnly }) {
  const [pre, setPre] = useState({});
  const [intra, setIntra] = useState([]);
  const [post, setPost] = useState({});
  const [saving, setSaving] = useState("");
  // The vitals last copied to IPD per section, seeded from the saved chart.
  const copiedRef = useRef({});

  useEffect(() => {
    fetchChart(session.id)
      .then((chart) => {
        setPre(chart.pre || {});
        setIntra(chart.intra || []);
        setPost(chart.post || {});
        copiedRef.current = { pre: vitalsKey(chart.pre), post: vitalsKey(chart.post) };
      })
      .catch((err) => toast.error(err?.body?.message || "Could not load the chart"));
  }, [session.id]);

  const save = async (section, value) => {
    setSaving(section);
    const payload = section === "intra" ? value.map(clean) : clean(value);
    const res = await saveChart(session.id, { [section]: payload });
    setSaving("");
    if (!res.ok) {
      toast.error(res.message || "Could not save the chart");
      return;
    }
    toast.success("Chart saved");
    if (section === "intra") return;
    const vitals = vitalsFrom(payload, session.admission_id, session.patient_id);
    const key = vitalsKey(payload);
    if (!vitals || key === copiedRef.current[section]) return;
    try {
      const copy = await addVitals(vitals);
      if (copy?.status === "Success") copiedRef.current[section] = key;
      else toast.warning(`Chart saved; IPD vitals not updated: ${copy?.message || ""}`);
    } catch (err) {
      toast.warning("Chart saved; IPD vitals not updated");
    }
  };

  const setRow = (index, patch) => setIntra(intra.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  return (
    <IpdSectionCard
      title="Dialysis chart"
      subtitle={readOnly ? "Read only — the patient has been discharged" : "Before, hourly during, and after the run. BP, pulse, temperature and weight also go to the IPD vitals."}
      sx={{ mb: 2.5 }}
    >
      <Box>
        <Summary pre={pre} post={post} />

        <ChartPart title="Before the run" subtitle="Pre-dialysis assessment" readOnly={readOnly}
          saving={saving === "pre"} onSave={() => save("pre", pre)}>
          <NumberGrid fields={PRE_NUMBERS} value={pre} onChange={setPre} disabled={readOnly} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "160px auto 1fr" }, gap: 2, mt: 2, alignItems: "end" }}>
            <IpdFormField label="Vascular access">
              <IpdTextField select fullWidth value={pre.access_type || ""} disabled={readOnly}
                onChange={(e) => setPre({ ...pre, access_type: e.target.value })}>
                {ACCESS_TYPES.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <FormControlLabel label="Access checked, patent" disabled={readOnly} sx={{ mb: 0.5 }}
              control={<Checkbox checked={Boolean(pre.access_ok)} onChange={(e) => setPre({ ...pre, access_ok: e.target.checked })} />} />
            <IpdFormField label="Notes">
              <IpdTextField fullWidth value={pre.notes || ""} disabled={readOnly}
                onChange={(e) => setPre({ ...pre, notes: e.target.value })} />
            </IpdFormField>
          </Box>
        </ChartPart>

        <ChartPart title="During the run" subtitle="One row per hourly observation" readOnly={readOnly}
          saving={saving === "intra"} onSave={() => save("intra", intra)}>
          <Box sx={{ overflowX: "auto", border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Time</TableCell>
                  {INTRA_NUMBERS.map(([key, label, unit]) => (
                    <TableCell key={key} sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>
                      {label}
                      {unit && <Typography component="span" sx={{ fontSize: 10.5, color: IPD_COLORS.textSecondary, ml: 0.5 }}>{unit}</Typography>}
                    </TableCell>
                  ))}
                  {!readOnly && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {intra.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={INTRA_NUMBERS.length + 2}>
                      <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, py: 1 }}>
                        No hourly observations yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {intra.map((row, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- rows have no id until saved
                  <TableRow key={index}>
                    <TableCell sx={{ minWidth: 110 }}>
                      <IpdTextField type="time" value={row.time || ""} disabled={readOnly} fullWidth
                        inputProps={{ "aria-label": "Time" }} onChange={(e) => setRow(index, { time: e.target.value })} />
                    </TableCell>
                    {INTRA_NUMBERS.map(([key, label]) => (
                      <TableCell key={key} sx={{ minWidth: 88 }}>
                        <IpdTextField type="number" value={row[key] ?? ""} disabled={readOnly} fullWidth
                          inputProps={{ "aria-label": label }} onChange={(e) => setRow(index, { [key]: e.target.value })} />
                      </TableCell>
                    ))}
                    {!readOnly && (
                      <TableCell padding="none">
                        <Tooltip title="Remove this row">
                          <IconButton aria-label="Remove hourly entry" onClick={() => setIntra(intra.filter((_, i) => i !== index))}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {!readOnly && (
            <IpdButton size="small" variant="outlined" sx={{ mt: 1.5 }}
              onClick={() => setIntra([...intra, { time: "" }])}>
              Add hourly entry
            </IpdButton>
          )}
        </ChartPart>

        <ChartPart title="After the run" subtitle="Post-dialysis assessment" readOnly={readOnly}
          saving={saving === "post"} onSave={() => save("post", post)}>
          <NumberGrid fields={POST_NUMBERS} value={post} onChange={setPost} disabled={readOnly} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "auto 1fr" }, gap: 2, mt: 2, alignItems: "end" }}>
            <FormControlLabel label="Access haemostasis achieved" disabled={readOnly} sx={{ mb: 0.5 }}
              control={(
                <Checkbox checked={Boolean(post.access_haemostasis_ok)}
                  onChange={(e) => setPost({ ...post, access_haemostasis_ok: e.target.checked })} />
              )} />
            <IpdFormField label="Complications">
              <IpdTextField fullWidth value={post.complications || ""} disabled={readOnly}
                onChange={(e) => setPost({ ...post, complications: e.target.value })} />
            </IpdFormField>
          </Box>
        </ChartPart>
      </Box>
    </IpdSectionCard>
  );
}
